/**
 * Da quale regione è una provincia.
 *
 * Serve perché `SedeCliente` **non ha una colonna `regione`**: ha `provincia`,
 * e basta. Il form Sopralluogo invece chiede la regione con una tendina, quindi
 * scegliendo una sede la regione o si ricava da qui o resta vuota — e resta
 * vuota è esattamente il difetto da togliere.
 *
 * Non è un'euristica: l'appartenenza di una provincia alla sua regione è un
 * fatto amministrativo, non un indovinello come sarebbe risalire al comune da
 * un nome scritto male. Le sigle coprono l'elenco `PROVINCE` del form
 * Sopralluogo, comprese le quattro province sarde soppresse nel 2016 (`CI`,
 * `VS`, `OG`, `OT`) che in quell'elenco ci sono ancora e che quindi possono
 * trovarsi scritte su una sede vecchia. `SU` non è nell'elenco ma è la sigla in
 * uso oggi per la stessa zona: se arriva da un'anagrafica esterna, va risolta
 * lo stesso.
 *
 * I nomi delle regioni sono scritti **esattamente** come nell'elenco `REGIONI`
 * di `Sopralluoghi.tsx`, che è ciò che la tendina accetta: «Emilia Romagna»
 * senza trattino, «Valle d'Aosta» con l'apostrofo. Cambiare l'uno senza l'altro
 * fa cadere la precompilazione in silenzio.
 */
const REGIONE_PER_PROVINCIA: Record<string, string> = {
  AG: "Sicilia", AL: "Piemonte", AN: "Marche", AO: "Valle d'Aosta", AP: "Marche",
  AQ: "Abruzzo", AR: "Toscana", AT: "Piemonte", AV: "Campania", BA: "Puglia",
  BG: "Lombardia", BI: "Piemonte", BL: "Veneto", BN: "Campania", BO: "Emilia Romagna",
  BR: "Puglia", BS: "Lombardia", BT: "Puglia", BZ: "Trentino Alto Adige", CA: "Sardegna",
  CB: "Molise", CE: "Campania", CH: "Abruzzo", CI: "Sardegna", CL: "Sicilia",
  CN: "Piemonte", CO: "Lombardia", CR: "Lombardia", CS: "Calabria", CT: "Sicilia",
  CZ: "Calabria", EN: "Sicilia", FC: "Emilia Romagna", FE: "Emilia Romagna", FG: "Puglia",
  FI: "Toscana", FM: "Marche", FR: "Lazio", GE: "Liguria", GO: "Friuli Venezia Giulia",
  GR: "Toscana", IM: "Liguria", IS: "Molise", KR: "Calabria", LC: "Lombardia",
  LE: "Puglia", LI: "Toscana", LO: "Lombardia", LT: "Lazio", LU: "Toscana",
  MB: "Lombardia", MC: "Marche", ME: "Sicilia", MI: "Lombardia", MN: "Lombardia",
  MO: "Emilia Romagna", MS: "Toscana", MT: "Basilicata", NA: "Campania", NO: "Piemonte",
  NU: "Sardegna", OG: "Sardegna", OR: "Sardegna", OT: "Sardegna", PA: "Sicilia",
  PC: "Emilia Romagna", PD: "Veneto", PE: "Abruzzo", PG: "Umbria", PI: "Toscana",
  PN: "Friuli Venezia Giulia", PO: "Toscana", PR: "Emilia Romagna", PT: "Toscana",
  PU: "Marche", PV: "Lombardia", PZ: "Basilicata", RA: "Emilia Romagna", RC: "Calabria",
  RE: "Emilia Romagna", RG: "Sicilia", RI: "Lazio", RM: "Lazio", RN: "Emilia Romagna",
  RO: "Veneto", SA: "Campania", SI: "Toscana", SO: "Lombardia", SP: "Liguria",
  SR: "Sicilia", SS: "Sardegna", SU: "Sardegna", SV: "Liguria", TA: "Puglia",
  TE: "Abruzzo", TN: "Trentino Alto Adige", TO: "Piemonte", TP: "Sicilia", TR: "Umbria",
  TS: "Friuli Venezia Giulia", TV: "Veneto", UD: "Friuli Venezia Giulia", VA: "Lombardia",
  VB: "Piemonte", VC: "Piemonte", VE: "Veneto", VI: "Veneto", VR: "Veneto",
  VS: "Sardegna", VT: "Lazio", VV: "Calabria",
};

/**
 * Nell'elenco `PROVINCE` del form Sopralluogo Roma non è `RM` ma la parola
 * «Roma» — l'unica voce su 110 scritta per esteso. È una stranezza di
 * quell'elenco, non una convenzione dell'app: ovunque altro (anagrafica, sedi,
 * installazioni) la provincia è la sigla di due lettere.
 *
 * Qui non la si corregge — cambiare l'opzione cambierebbe il valore salvato e i
 * sopralluoghi già scritti direbbero una cosa diversa dai nuovi. Si traduce
 * soltanto al momento di precompilare, perché una tendina a cui si assegna un
 * valore che non è fra le sue opzioni non mostra niente: la sede avrebbe una
 * provincia e il campo resterebbe vuoto, che è il difetto che stiamo togliendo.
 */
export function provinciaPerElenco(sigla: string | null | undefined): string {
  const s = (sigla ?? "").trim().toUpperCase();
  if (!s) return "";
  return s === "RM" ? "Roma" : s;
}

/** Regione della provincia, o `""` se la sigla non è nota (non si tira a indovinare). */
export function regionePerProvincia(sigla: string | null | undefined): string {
  const s = (sigla ?? "").trim().toUpperCase();
  if (!s) return "";
  // «Roma» per esteso arriva dai sopralluoghi già salvati: vedi sopra.
  if (s === "ROMA") return "Lazio";
  return REGIONE_PER_PROVINCIA[s] ?? "";
}
